import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { BroadcastIcon, UnbroadcastIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { getFormattedEventName } from 'src/utils/eventWithDataUtils';
import {
  getFormattedLocation,
  getQuantityNumberFormat,
} from 'src/utils/format';
import { formatPercent } from 'src/utils/numberFormatter';
import { getInventoryListingMetricsDisplayStrings } from 'src/utils/ticketMetricUtils';
import {
  Event,
  Feature,
  ListingMetrics,
  Performer,
  SaleMetrics,
  TicketGroupMetrics,
  Venue,
} from 'src/WebApiController';

import * as styles from './MobileEventTile.css';

export const EventTileDateCard = ({
  eventDate,
}: {
  eventDate?: string | null;
}) => {
  const { date, weekday, time } = useMemo(() => {
    const eventStartDate = eventDate ? new Date(eventDate) : new Date();
    return {
      date: format(eventStartDate, 'M/d'),
      weekday: format(eventStartDate, 'EEE'),
      time: format(eventStartDate, 'h:mm aaa').toUpperCase(),
    };
  }, [eventDate]);
  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      className={styles.eventDateContainer}
    >
      <span className={styles.eventDateBoldDate}>{date}</span>
      <span>{weekday}</span>
      <span>{time}</span>
    </Stack>
  );
};

export const EventTileInfoHeader = ({
  event,
  venue,
  performer,
  time,
}: {
  event: Event;
  performer?: Performer;
  venue?: Venue;
  time?: string;
}) => {
  const [eventName, eventNameConnector, eventSubName] = getFormattedEventName({
    event,
    performer,
  });
  return (
    <Stack
      direction="column"
      width="full"
      alignItems="center"
      style={{ overflow: 'hidden' }}
    >
      <div className={styles.eventHeaderEventNameContainer}>
        {eventNameConnector && !eventSubName && (
          <span className={styles.eventHeaderNameConnector}>
            {eventNameConnector}{' '}
          </span>
        )}
        <span>{eventName}</span>
        {eventNameConnector && eventSubName && (
          <>
            <span className={styles.eventHeaderNameConnector}>
              {eventNameConnector}{' '}
            </span>
            <span>{eventSubName}</span>
          </>
        )}
      </div>
      <div className={styles.eventHeaderVenueNameContainer}>
        {time && (
          <>
            <span>{`${time}`}</span>
            <span> @ </span>
          </>
        )}
        {venue && (
          <>
            <span className={styles.venueName}>{venue?.name}</span>
            <span> {getFormattedLocation(venue)}</span>
          </>
        )}
      </div>
    </Stack>
  );
};

export const EventTileListingMetrics = ({
  metrics,
}: {
  metrics: ListingMetrics;
}) => {
  const hasBlockInventorySummaryStatsFeature = useUserHasFeature(
    Feature.BlockInventorySummaryStats
  );
  const pandLText = useContent(ContentId.PandLTitle);
  const totalUnlistedQuantityText = useContent(
    ContentId.NumberOfUnlistedTicketsDescription
  );

  const {
    formattedTotalGrossProfit,
    formattedTotalUnlistedQuantity,
    formattedTotalListedQuantity,
  } = getInventoryListingMetricsDisplayStrings(metrics, metrics.currency);

  const profitOrLoss = useMemo(
    () => (metrics.pandL >= 0 ? 'profit' : 'loss'),
    [metrics.pandL]
  );

  return (
    <Stack alignItems="center" gap="m">
      {!hasBlockInventorySummaryStatsFeature && (
        <Stack
          alignItems="center"
          justifyContent="center"
          gap="s"
          className={clsx(
            styles.eventHeaderMetricsContainer,
            styles.eventHeaderMetricsProfitAndLoss[profitOrLoss]
          )}
          title={pandLText}
        >
          <span>{formattedTotalGrossProfit}</span>
        </Stack>
      )}
      <Stack
        alignItems="center"
        justifyContent="center"
        className={clsx(styles.eventHeaderMetricsBroadcastContainer)}
        title={totalUnlistedQuantityText}
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          gap="s"
          className={clsx(
            styles.eventHeaderMetricsContainer,
            styles.eventHeaderMetricsBroadcast
          )}
        >
          <BroadcastIcon size={vars.iconSize.s} />
          <span>{formattedTotalListedQuantity}</span>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="center"
          gap="s"
          className={clsx(
            styles.eventHeaderMetricsContainer,
            styles.eventHeaderMetricsBroadcast
          )}
        >
          <UnbroadcastIcon size={vars.iconSize.s} />
          <span>{formattedTotalUnlistedQuantity}</span>
        </Stack>
      </Stack>
    </Stack>
  );
};

export const EventTilePurchaseMetrics = ({
  metrics,
}: {
  metrics: TicketGroupMetrics;
}) => {
  const totalCostText = useContent(ContentId.TotalCost);
  const ticketsText = useContent(ContentId.Tickets);

  const getFractionDigits = useCallback(
    (val: number) => (val >= 1_000 ? 1 : 0),
    []
  );

  const getCurrencyNumberFormat = useCallback(
    (val: number) =>
      Intl.NumberFormat(undefined, {
        style: 'currency',
        notation: 'compact',
        currency: metrics.currency ?? undefined,
        minimumFractionDigits: getFractionDigits(val),
        maximumFractionDigits: getFractionDigits(val),
      }),
    [getFractionDigits, metrics.currency]
  );

  const { formattedTotalCost, formattedTicketQuantity } = useMemo(() => {
    const { ttlCst, tktQty } = metrics;
    const sanitizedCost = ttlCst ?? 0;
    return {
      formattedTotalCost:
        getCurrencyNumberFormat(sanitizedCost).format(sanitizedCost),
      formattedTicketQuantity: getQuantityNumberFormat().format(tktQty ?? 0),
    };
  }, [getCurrencyNumberFormat, metrics]);

  return (
    <Stack alignItems="center" gap="m">
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="s"
        className={clsx(
          styles.eventHeaderMetricsContainer,
          styles.eventHeaderMetricsBroadcast
        )}
      >
        <span className={styles.eventHeaderMetricsProfitAndLossLabel}>
          {totalCostText}
        </span>
        <span>{formattedTotalCost}</span>
      </Stack>
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="s"
        className={clsx(
          styles.eventHeaderMetricsContainer,
          styles.eventHeaderMetricsBroadcast
        )}
      >
        <span className={styles.eventHeaderMetricsProfitAndLossLabel}>
          {ticketsText}
        </span>
        <span>{formattedTicketQuantity}</span>
      </Stack>
    </Stack>
  );
};

export const EventTileSalesMetrics = ({
  metrics,
  saleCount,
}: {
  metrics: SaleMetrics;
  saleCount: number;
}) => {
  const marginText = useContent(ContentId.Margin);
  const filledText = useContent(ContentId.Filled);
  const saleText = useContent(ContentId.Sale);
  const salesText = useContent(ContentId.Sales);

  const { formattedMargin, formattedFilled, formattedSaleCount } =
    useMemo(() => {
      const { marg, qtySold, saleUnfilled } = metrics;
      const sanitizedMargin = marg ?? 0;
      return {
        formattedMargin: formatPercent(sanitizedMargin),
        formattedFilled: `${qtySold - saleUnfilled}/${qtySold}`,
        formattedSaleCount: getQuantityNumberFormat().format(saleCount),
      };
    }, [metrics, saleCount]);

  return (
    <Stack alignItems="center" gap="m">
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="s"
        className={clsx(
          styles.eventHeaderMetricsContainer,
          styles.eventHeaderMetricsBroadcast
        )}
      >
        <span className={styles.eventHeaderMetricsProfitAndLossLabel}>
          {marginText}
        </span>
        <span>{formattedMargin}</span>
      </Stack>
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="s"
        className={clsx(
          styles.eventHeaderMetricsContainer,
          styles.eventHeaderMetricsBroadcast
        )}
      >
        <span className={styles.eventHeaderMetricsProfitAndLossLabel}>
          {filledText}
        </span>
        <span>{formattedFilled}</span>
      </Stack>
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="s"
        className={clsx(
          styles.eventHeaderMetricsContainer,
          styles.eventHeaderMetricsBroadcast
        )}
      >
        <span>{formattedSaleCount}</span>
        <span>{saleCount > 1 ? salesText : saleText}</span>
      </Stack>
    </Stack>
  );
};
